<template>
  <v-container fluid class="no-print" px-0 pt-0 pb-4>
    <v-row dense justify="end">
      <v-col cols="12" md="auto" :order="1" :order-md="0">
        <v-text-field
          v-if="showSearch"
          v-model="searchValue"
          class="inline-search slim-control"
          prepend-inner-icon="search"
          :placeholder="$m.grid_search"
          color="brand02"
        />
      </v-col>
      <v-col v-if="showExpandAll" cols="auto">
        <bm-btn
          button-type="secondary"
          data-qa="toggle-expand-all"
          @click="$emit('toggle-expand-all')"
        >
          <template v-if="allRowsExpanded">
            <v-icon :left="$vuetify.breakpoint.smAndUp">arrow_upward</v-icon>
            {{ $m.grid_collapse_all }}
          </template>
          <template v-else>
            <v-icon :left="$vuetify.breakpoint.smAndUp">arrow_downward</v-icon>
            {{ $m.grid_expand_all }}
          </template>
        </bm-btn>
      </v-col>
      <template v-if="!hideFilter || !hideSort || showManageColumns">
        <v-col cols="auto" class="grid-controls-actions">
          <v-badge
            style="z-index: 1"
            :value="activelyFilteredColumns.length > 0"
            :content="activelyFilteredColumns.length"
            color="primaryChip"
            overlap
          >
            <bm-btn
              v-if="!hideFilter || !hideSort"
              button-type="secondary"
              @click="showFilterDrawer = !showFilterDrawer"
            >
              <v-icon :left="$vuetify.breakpoint.smAndUp">filter_list</v-icon>
              {{ $vuetify.breakpoint.smAndUp ? sortAndOrFilterText : "" }}
            </bm-btn>
          </v-badge>
          <bm-btn
            v-if="showManageColumns"
            button-type="secondary"
            @click="showColumnSelectorDrawer = !showColumnSelectorDrawer"
          >
            <v-icon :left="$vuetify.breakpoint.smAndUp">view_column</v-icon>
            {{ $vuetify.breakpoint.smAndUp ? $m.grid_manage_columns : "" }}
          </bm-btn>
          <bm-btn
            v-if="showExport"
            button-type="secondary"
            @click="showExportDrawer = !showExportDrawer"
          >
            <v-icon :left="$vuetify.breakpoint.smAndUp">file_download</v-icon>
            {{ $vuetify.breakpoint.smAndUp ? $m.grid_export : "" }}
          </bm-btn>
          <slot
            v-if="$slots['additional-controls']"
            name="additional-controls"
          />
        </v-col>
      </template>
    </v-row>
    <!-- Column selector -->
    <v-navigation-drawer
      v-if="showColumnSelectorDrawer"
      v-model="showColumnSelectorDrawer"
      :aria-hidden="!showColumnSelectorDrawer"
      :hidden="!showColumnSelectorDrawer"
      class="grid-drawer manage-columns"
      temporary
      hide-overlay
      right
      app
    >
      <v-list-item :aria-hidden="!showColumnSelectorDrawer">
        <v-list-item-content>
          <v-list-item-title class="bm-headline">
            {{ $m.grid_manage_columns }}
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn icon>
            <v-icon @click="showColumnSelectorDrawer = false">close</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>

      <v-divider></v-divider>

      <v-list-item>
        <v-list-item-content class="py-0">
          <bm-btn
            button-type="ghost"
            :t="$m.keys.grid_clear_all_filters"
            @click="resetAllFilters"
          />
        </v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>

      <v-list-item>
        <v-list-item-content class="d-block">
          <bm-checkbox
            v-for="col in removableColumns"
            :key="col.columnKey"
            :label="col.headingText"
            :disabled="!col.removable"
            :input-value="!col.removed"
            @change="col.remove(col.removed)"
          />
        </v-list-item-content>
      </v-list-item>
    </v-navigation-drawer>
    <!-- Filter selector -->
    <v-navigation-drawer
      v-if="showFilterDrawer"
      v-model="showFilterDrawer"
      :aria-hidden="!showFilterDrawer"
      :hidden="!showFilterDrawer"
      class="grid-drawer"
      temporary
      hide-overlay
      right
      app
    >
      <v-list-item :aria-hidden="!showFilterDrawer">
        <v-list-item-content>
          <v-list-item-title class="bm-headline">
            {{ sortAndOrFilterText }}
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn icon>
            <v-icon @click="showFilterDrawer = false">close</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>

      <v-divider></v-divider>

      <template v-if="activelyFilteredColumns.length > 0">
        <v-list-item>
          <v-list-item-content>
            <v-container fluid pa-0>
              <v-row dense>
                <v-col
                  v-for="[key] in activelyFilteredColumns"
                  :key="key"
                  cols="auto"
                >
                  <bm-chip
                    :text="$t(key).toString()"
                    color="primaryChip"
                    pill
                    small
                    close
                    close-icon="close"
                    @click:close="deleteFilter(key)"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>
      </template>

      <v-list-item>
        <v-list-item-content class="py-0">
          <bm-btn
            button-type="ghost"
            :t="$m.keys.grid_clear_all_filters"
            @click="resetAllFilters"
          />
        </v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>

      <v-card
        v-for="col in sortableAndFilterableColumns"
        :key="col.columnKey"
        class="control-box"
        outlined
      >
        <v-card-title
          class="bm-headline clickable"
          style="word-break: break-word"
          @click="col.toggleExpanded()"
        >
          {{ col.headingText }}
          <v-spacer />
          <v-icon>
            {{ col.expanded ? "keyboard_arrow_up" : "keyboard_arrow_down" }}
          </v-icon>
        </v-card-title>
        <v-divider v-if="col.expanded"></v-divider>
        <v-expand-transition>
          <v-card-text v-if="col.expanded" class="sort-controls pb-4">
            <template v-if="!hideSort">
              <bm-checkbox
                block
                width="100%"
                :label="$m.grid_sort_ascending"
                :input-value="col.sortedAsc"
                @change="(val) => (val ? col.sort(false) : col.clearSort())"
              />
              <bm-checkbox
                block
                width="100%"
                :label="$m.grid_sort_descending"
                :input-value="col.sortedDesc"
                @change="(val) => (val ? col.sort(true) : col.clearSort())"
              />

              <v-divider />
            </template>

            <component
              :is="col.filterComponent"
              v-if="!hideFilter && col.filterComponent"
              :grid-state="gridState"
              :column-config="gridConfiguration.columns[col.columnKey]"
              :column-key="col.columnKey"
            />
          </v-card-text>
        </v-expand-transition>
      </v-card>
    </v-navigation-drawer>
    <!-- Export Draw -->
    <v-navigation-drawer
      v-if="showExportDrawer"
      v-model="showExportDrawer"
      :aria-hidden="!showExportDrawer"
      :hidden="!showExportDrawer"
      class="grid-drawer"
      temporary
      hide-overlay
      right
      app
    >
      <v-list-item :aria-hidden="!showExportDrawer">
        <v-list-item-content>
          <v-list-item-title class="bm-headline">
            {{ $m.grid_export }}
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn icon>
            <v-icon @click="showExportDrawer = false">close</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>

      <v-divider></v-divider>
      <v-list-item>
        <v-list-item-content class="py-0">
          <bm-btn block @click="exportGrid('csv')">CSV</bm-btn>
        </v-list-item-content>
      </v-list-item>
      <v-list-item>
        <v-list-item-content class="py-0">
          <bm-btn block @click="exportGrid('pdf')">PDF</bm-btn>
        </v-list-item-content>
      </v-list-item>
      <v-list-item>
        <v-list-item-content class="py-0">
          <bm-btn block @click="exportGrid('googleSheets')"
            >Google Sheets</bm-btn
          >
        </v-list-item-content>
      </v-list-item>
    </v-navigation-drawer>
  </v-container>
</template>

<script lang="ts">
import type {
  BmGridConfiguration,
  BmGridExportEvent,
  BmGridState,
  GridExportFormat,
} from "@/components/controls/grid/BmGridData";
import type { VueConstructor, PropType } from "vue";
import Vue, { defineComponent } from "vue";
import { useRoot } from "@/components/composables/root";
import { useRouter } from "@/router/router";
import { indexedKeyComparer } from "@/components/controls/grid/BmGridComparers";
import { debounce } from "lodash-es";
import { translate } from "@/plugins/i18n";
import Key from "@/app/types/common/key";

interface RemovableColumnState {
  columnKey: string;
  headingText: string;
  removable: boolean;
  removed: boolean;
  remove: (remove: boolean) => void;
}

interface FilterSortColumnState {
  columnKey: string;
  expanded: boolean;
  toggleExpanded: () => void;
  headingText: string;
  sort: (descending: boolean) => void;
  clearSort: () => void;
  sortedAsc: boolean;
  sortedDesc: boolean;
  filterComponent?: VueConstructor;
}

/**
 * A control panel that accompanies the BmGrid component.
 * Provides GUI for instant text search, showing/removing columns, and filters.
 */
export default defineComponent({
  name: "BmGridControlPanel",
  props: {
    gridState: {
      type: Object as PropType<BmGridState>,
      required: true,
    },
    gridConfiguration: {
      type: Object as PropType<BmGridConfiguration>,
      required: true,
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    hideFilter: {
      type: Boolean,
      default: false,
    },
    hideSort: {
      type: Boolean,
      default: false,
    },
    showManageColumns: {
      type: Boolean,
      default: true,
    },
    showExport: {
      type: Boolean,
      default: true,
    },
    customPrintTitle: {
      type: [Function, String] as PropType<(() => string) | string | null>,
      required: false,
      default: null,
    },
    showExpandAll: {
      type: Boolean,
      default: false,
    },
    allRowsExpanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchValue: "",
      showColumnSelectorDrawer: false,
      showFilterDrawer: false,
      showExportDrawer: false,
      shownSortAndFilterBoxes: {} as { [key: string]: boolean },
    };
  },
  computed: {
    sortAndOrFilterText() {
      if (this.hideFilter) {
        return this.$m.grid_only_sort;
      }

      if (this.hideSort) {
        return this.$m.grid_only_filter;
      }

      return this.$m.grid_filter;
    },
    sortedColumns() {
      return Object.entries(this.gridConfiguration.columns).sort(
        indexedKeyComparer(this.gridState.columnOrder, (x) => x[0])
      );
    },
    activelyFilteredColumns() {
      return Object.entries(this.gridState.columnFilters);
    },
    sortableAndFilterableColumns(): FilterSortColumnState[] {
      const gridState = this.gridState;
      return this.sortedColumns
        .filter(
          ([_, column]) =>
            column.sortableAndFilterable && column.showInGridControls
        )
        .map(([key, column]) => {
          return {
            columnKey: key,
            expanded: this.shownSortAndFilterBoxes[key] ?? false,
            headingText: column.headingText,
            toggleExpanded: () => {
              Vue.set(
                this.shownSortAndFilterBoxes,
                key,
                !(this.shownSortAndFilterBoxes[key] ?? false)
              );
            },
            sort: (descending: boolean) => {
              // we only assign single sort criteria when originated from user interaction. They can 'stack' manually if
              // desired.
              // eslint-disable-next-line vue/no-mutating-props
              this.gridState.sortBy = [
                {
                  columnKey: key,
                  descending: descending,
                },
              ];
            },
            clearSort() {
              gridState.sortBy = [];
            },
            get sortedAsc(): boolean {
              return gridState.sortBy.some(
                (sortBy) =>
                  sortBy.columnKey === this.columnKey && !sortBy.descending
              );
            },
            get sortedDesc(): boolean {
              return gridState.sortBy.some(
                (sortBy) =>
                  sortBy.columnKey === this.columnKey && sortBy.descending
              );
            },
            filterComponent: column.columnFilter?.columnFilterComponent,
          };
        });
    },
    removableColumns(): RemovableColumnState[] {
      return this.sortedColumns
        .filter(([key, column]) => {
          return key !== "actions" && column.showInGridControls;
        })
        .map(([key, column]) => {
          return {
            columnKey: key,
            headingText: column.headingText,
            removable:
              column.removable &&
              !this.gridState.groupBy.some((g) => g === key),
            removed: this.gridState.removedColumnKeys.some((g) => g === key),
            remove: (remove: boolean) => {
              const itemIdx = this.gridState.removedColumnKeys.indexOf(key);
              if (remove && itemIdx !== -1) {
                // eslint-disable-next-line vue/no-mutating-props
                this.gridState.removedColumnKeys.splice(itemIdx, 1);
              } else if (!remove && itemIdx === -1) {
                // eslint-disable-next-line vue/no-mutating-props
                this.gridState.removedColumnKeys.push(key);
              }
            },
          };
        });
    },
  },
  watch: {
    searchValue: {
      // @ts-ignore
      handler: "searchWatcher",
    },
  },
  created() {
    this.shownSortAndFilterBoxes = Object.fromEntries(
      Object.entries(this.gridConfiguration.columns)
        .filter(
          ([key, column]) =>
            column.sortableAndFilterable && this.gridState.columnFilters[key]
        )
        .map(([key]) => {
          return [key, true];
        })
    );
    this.searchValue = this.gridState.gridSearchFilter ?? "";
  },
  methods: {
    searchWatcher: debounce(function (this: any) {
      // eslint-disable-next-line vue/no-mutating-props
      this.gridState.gridSearchFilter = this.searchValue;
    }, 300),

    resetAllFilters() {
      this.searchValue = "";
      this.showColumnSelectorDrawer = false;
      this.showFilterDrawer = false;
      this.gridState.reset();
    },
    deleteFilter(key: string) {
      this.gridState.deleteFilter(key);
    },
    exportGrid(format: GridExportFormat) {
      const root = useRoot();

      const { router } = useRouter();

      const customTitle =
        this.customPrintTitle instanceof Function
          ? this.customPrintTitle()
          : this.customPrintTitle;

      const fileTitle =
        customTitle ??
        this.gridConfiguration.title ??
        translate(Key.fromString(router.currentRoute.name + ""));

      const event: BmGridExportEvent = {
        filename: fileTitle + "." + format,
        fileTitle,
        format: format,
        stateId: this.gridState.stateId,
      };
      root.$emit("bm-grid-export", event);
    },
  },
});
</script>

<style scoped lang="sass">
.inline-search
  padding-top: 0

// Make the buttons compress in mobile
:deep(.v-btn)
  min-width: 32px !important
  padding: 0 8px !important

// Drawer

.manage-columns .bm-checkbox
  margin-left: 8px
  margin-top: 10px

.control-box
  margin: 10px

.control-box :deep(.v-card__title)
  padding: 8px

.control-box :deep(.v-card__text)
  padding: 5px 16px

.control-box .sort-controls hr
  margin-top: 10px

.control-box .clickable
  cursor: pointer

.grid-controls-actions
  min-width: fit-content
  display: flex
  flex-flow: row
  column-gap: 4px
  justify-content: flex-end
</style>
